import { create } from "zustand";

/**
 * Excel-preview store (фича 028) — управляет окнами `/preview.html` для
 * исходных файлов нарядов / актов. Один файл — одно окно (target=`preview-${fileId}`),
 * переключение строки внутри открытого окна идёт через `postMessage` без reload.
 *
 * См. `apps/web/public/preview.html` + `preview.js` (W2).
 *
 * ## Восстановление после reload SPA (T-C4.1)
 *
 * `windows` — обычный in-memory Map; при reload SPA он пустеет, даже если
 * preview-вкладка по-прежнему открыта пользователем рядом. Чтобы не плодить
 * дубликаты, мы используем `target=preview-${fileId}` (named window target).
 * Браузер сам переиспользует существующую вкладку с тем же `target`, если она
 * открыта — даже после reload родительской SPA. Поэтому повторный
 * `window.open(url, target)` после reload вернёт ссылку на ту же вкладку,
 * и далее `postMessage` отработает корректно.
 *
 * ## TOCTOU edge
 *
 * Между `existing.closed` check (T1) и `existing.postMessage(...)` (T2)
 * пользователь может закрыть вкладку: получим throw `InvalidStateError` /
 * silent no-op в зависимости от браузера. Лечим try/catch вокруг postMessage
 * и fallback на новый `window.open()`. Аналогично — между `window.open()`
 * (вернул `null` если popup-blocker) и сохранением в Map: проверяем `win`.
 * Полностью устранить TOCTOU нельзя — это inherent для async window-API.
 */

interface OpenArgs {
	fileId: number;
	row: number;
	sheet?: string | null;
}

interface PreviewState {
	/** fileId → WindowProxy открытой preview-вкладки. */
	windows: Map<number, Window>;
	/**
	 * Открыть preview для строки. Если окно того же fileId уже открыто и
	 * не закрыто пользователем — переиспользуется через postMessage `{type:'goto'}`.
	 * Иначе открывается новая вкладка через `window.open(..., 'preview-${fileId}')`.
	 *
	 * После reload SPA Map пустой, но `target=preview-${fileId}` позволяет
	 * браузеру переиспользовать уже открытую вкладку (см. JSDoc модуля).
	 */
	openOrFocus: (args: OpenArgs) => void;
}

export const usePreviewStore = create<PreviewState>((set, get) => ({
	windows: new Map(),
	openOrFocus: ({ fileId, row, sheet }) => {
		const target = `preview-${fileId}`;
		const existing = get().windows.get(fileId);
		if (existing && !existing.closed) {
			// TOCTOU: пользователь мог закрыть вкладку между .closed-check и postMessage.
			// Если postMessage упадёт — проваливаемся в новый window.open() ниже.
			try {
				existing.postMessage(
					{ type: "goto", row, sheet: sheet ?? null },
					window.location.origin,
				);
				try {
					existing.focus();
				} catch {
					// focus может бросать в headless / при ограничениях браузера —
					// это не критично, окно уже получит сообщение.
				}
				return;
			} catch {
				// postMessage упал (вкладка закрыта между чеком и вызовом) — открываем заново.
			}
		}

		// После reload SPA `windows` Map пустой, но именованный target=`preview-${fileId}`
		// позволяет браузеру переиспользовать ранее открытую вкладку без новых параметров.
		// Если пользователь до этого был на preview.html?fileId=…&row=… — браузер вернёт
		// ту же вкладку, и preview.js получит свежий URL/postMessage от нового SPA.
		const params = new URLSearchParams({
			fileId: String(fileId),
			row: String(row),
		});
		if (sheet) params.set("sheet", sheet);
		const url = `/preview.html?${params.toString()}`;
		const win = window.open(url, target);
		if (win) {
			const next = new Map(get().windows);
			next.set(fileId, win);
			set({ windows: next });
			// Если вкладка уже была открыта (target reuse после reload),
			// preview.js получит новый URL через .location перезаписи браузером —
			// но если SPA уже подгружена, postMessage надёжнее.
			// Откладываем: window.open → если страница ещё грузится, она сама
			// прочитает row/sheet из URL; если перезагружена — её main() заново отработает.
		}
	},
}));
